import { toast } from 'react-toastify';
import axios from 'axios';
import i18n from '../../i18n';

export interface ApiErrorResponse {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  code?: string;
  instance?: string;
  traceId?: string;
  additionalData?: unknown;
}

export const handleApiError = (
  error: unknown,
  defaultMessage?: string
): void => {
  let errorData: ApiErrorResponse | undefined;

  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    errorData = error.response?.data;
  } else if (typeof error === 'object' && error !== null && 'data' in error) {
    errorData = (error as { data: ApiErrorResponse }).data;
  } else if (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as { error: unknown }).error === 'object'
  ) {
    errorData = (error as { error: ApiErrorResponse }).error;
  }

  const message =
    defaultMessage || i18n.t('common.error', 'An unknown error occurred');

  if (errorData?.code) {
    switch (errorData.code) {
      case 'EMAIL_ALREADY_CONFIRMED':
        toast.warning(
          i18n.t(
            'restaurant-users.resend-email.already-confirmed',
            'This email has already been confirmed.'
          )
        );
        return;

      case 'USER_NOT_FOUND':
        toast.error(
          i18n.t('restaurant-users.errors.user-not-found', 'User not found.')
        );
        return;

      default:
        toast.error(message);
    }
  } else {
    toast.error(message);
  }
};

// Helper to extract error message from error object
export const getErrorMessage = (
  error: unknown,
  defaultMessage: string = ''
): string => {
  if (!error) return defaultMessage;

  if (axios.isAxiosError<ApiErrorResponse>(error) && error.response?.data) {
    const errorData = error.response.data;
    return errorData.detail || errorData.title || defaultMessage;
  }

  if (error instanceof Error) {
    return error.message || defaultMessage || 'An unknown error occurred';
  }

  return defaultMessage || 'An unknown error occurred';
};
